import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import AnalysisProgress from '../components/analyzer/AnalysisProgress';

export default function ScanPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [progress, setProgress] = useState({ completed: 0, total: 37 });
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = `${t('scan.title')} | SpamSandbox`;
    return () => { document.title = 'SpamSandbox'; };
  }, [t]);

  useEffect(() => {
    const analysisId = location.state?.analysisId || id;
    if (!analysisId) {
      setError(t('scan.missingId'));
      return;
    }

    let pollInterval = null;
    let timeout = null;

    const poll = async () => {
      try {
        const res = await api.getAnalysisStatus(analysisId);
        const data = res.data;

        setProgress({
          completed: data.sources_completed || 0,
          total: data.sources_total || 37,
        });

        if (data.is_complete) {
          clearInterval(pollInterval);
          clearTimeout(timeout);
          const identifier = location.state?.identifier;
          const urlId = identifier ? encodeURIComponent(identifier) : analysisId;
          navigate(`/result/${urlId}`, { replace: true, state: { analysisId } });
        }
      } catch (err) {
        if (err.status === 404 || err.response?.status === 404) {
          setError(t('scan.notFound'));
          clearInterval(pollInterval);
          clearTimeout(timeout);
        }
      }
    };

    api.getAnalysis(analysisId).then(res => {
      if (res.data.is_complete) {
        const identifier = location.state?.identifier;
        const urlId = identifier ? encodeURIComponent(identifier) : analysisId;
        navigate(`/result/${urlId}`, { replace: true, state: { analysisId } });
        return;
      }
      setProgress({
        completed: res.data.sources_completed || 0,
        total: res.data.sources_total || 37,
      });
      pollInterval = setInterval(poll, 2000);
    }).catch(() => {
      pollInterval = setInterval(poll, 2000);
    });

    timeout = setTimeout(() => {
      if (pollInterval) {
        clearInterval(pollInterval);
        setError(t('scan.expired'));
      }
    }, 300000);

    return () => {
      clearInterval(pollInterval);
      clearTimeout(timeout);
    };
  }, [id, location.state, navigate, t]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <div className="glass-card p-8">
          <svg className="w-16 h-16 mx-auto mb-4 text-danger" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
          <h2 className="text-xl text-white font-semibold mb-2">{t('common.error')}</h2>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-20">
      <Link to="/" className="flex items-center gap-2 text-gray-400 hover:text-safe transition-colors mb-6 text-sm">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
        </svg>
        {t('scan.backToHome')}
      </Link>
      <AnalysisProgress completed={progress.completed} total={progress.total} />
    </div>
  );
}
